import React from 'react';

import { useViewModel } from './useViewModel';
import { Info } from './Info';
import { useLocale } from '../../../context/LocaleContext';
import { GiftWrapLoader } from '../../GiftWrapLoader';
import { usePage } from '../../../context/PageContext';
import { pages } from '../../../context/pages';

import styles from './footer.module.css';

/**
 * GiftWrapFooter component displays the footer for the gift wrap journey, including navigation and toast notifications.
 * Dynamically renders buttons based on the current page configuration from PageContext.
 *
 * @returns {import('react').JSX.Element}
 */
export function Footer() {
  const {
    priceModalOpen,
    handleArrowClick,
    handleAddGiftWrap,
    handleSkipGiftWrap,
    displayedTotalPrice,
    personalisationChoices,
    addToCartLoading,
    handleButtonClick,
  } = useViewModel();
  const { formatPrice } = useLocale();
  const { currentPage, getPageSelection, getPageAllSelected, getPageHasSelections } = usePage();

  // Get the current page configuration.
  const pageConfig = pages[currentPage];

  // Determine if the current page has selections based on page type.
  const hasSelections = getPageHasSelections(currentPage);
  const allSelected = getPageAllSelected(currentPage);

  /**
   * Determines if a button should be disabled based on its configuration and page selections.
   * @param {Object} button - The button configuration object.
   * @returns {boolean} - Whether the button should be disabled.
   */
  const isButtonDisabled = (button) => {
    if (typeof button.disabled === 'boolean') {
      return button.disabled;
    }
    if (typeof button.disabled === 'object') {
      // Support hasSelections/noSelections and allSelected keys.
      if (allSelected && typeof button.disabled.allSelected === 'boolean') {
        return button.disabled.allSelected;
      }
      return hasSelections ? button.disabled.hasSelections : button.disabled.noSelections;
    }
    return false;
  };

  /**
   * Determines if a button should be hidden based on its configuration.
   * @param {Object} button - The button configuration object.
   * @returns {boolean} - Whether the button should be hidden.
   */
  const isButtonHidden = (button) => {
    if (typeof button.hidden === 'boolean') {
      return button.hidden;
    }
    if (typeof button.hidden === 'object') {
      if (allSelected && typeof button.hidden.allSelected === 'boolean') {
        return button.hidden.allSelected;
      }
      return hasSelections ? button.hidden.hasSelections : button.hidden.noSelections;
    }
    return false;
  };

  /**
   * Gets the appropriate CSS classes for a button based on its index and state.
   * @param {number} index - The button index.
   * @param {boolean} disabled - Whether the button is disabled.
   * @returns {string} - The CSS class string.
   */
  const getButtonClasses = (index, disabled) => {
    if (index === 0) {
      return 'button button--primary button--orange' + (disabled ? ' button--disabled' : '');
    }
    return 'button button--secondary button--black button--full-width';
  };

  return (
    <footer className={styles['giftWrap__footer']}>
      <div className={styles['giftWrap__footer-inner']}>
        <div className={styles['giftWrap__footer-detail']}>
          <span className={styles['giftWrap__footer-detail-label']}>Current Total:</span>
          <span className={styles['giftWrap__footer-price']}>
            {formatPrice({ amount: displayedTotalPrice })}
            <button
              className={`${styles['giftWrap__footer-arrow']} ${priceModalOpen ? styles['giftWrap__footer-arrow--open'] : ''}`}
              onClick={handleArrowClick}
              type="button"
              aria-label="Toggle price details"
              style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="icon icon-chev"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.21726 6.52756C8.37346 6.37135 8.62673 6.37135 8.78294 6.52756L12.7829 10.5276C12.9391 10.6838 12.9391 10.937 12.7829 11.0932C12.6267 11.2495 12.3735 11.2495 12.2173 11.0932L8.5001 7.37609L4.78294 11.0932C4.62673 11.2495 4.37346 11.2495 4.21725 11.0932C4.06105 10.937 4.06105 10.6838 4.21725 10.5276L8.21726 6.52756Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </span>
        </div>
        <div className={`${styles['giftWrap__footer-actions']} ${styles['giftWrap__footer-actions--stacked']}`}>
          {pageConfig?.buttons?.map((button, index) => {
            const disabled = isButtonDisabled(button);
            const hidden = isButtonHidden(button);

            if (hidden) {
              return null;
            }

            return (
              <button
                key={index}
                className={getButtonClasses(index, disabled)}
                onClick={() => handleButtonClick(button, currentPage)}
                disabled={disabled || addToCartLoading}
                type="button"
              >
                {addToCartLoading && index === 0 ? 'Adding to bag...' : button.label}
              </button>
            );
          })}
        </div>
      </div>
      <Info />
      <GiftWrapLoader isVisible={addToCartLoading} />
    </footer>
  );
}
